/**
 * PropertyReviews — review monitoring across Google + Trustpilot.
 *
 * Reads from DFS-scraped review endpoints (Google reviews + Trustpilot
 * reviews — both via the existing /capabilities/{vendor}/sample API).
 * The only path to review data we have today (GBP API is quota-blocked).
 */
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "@tremor/react";
import {
  MessageSquare, Star, ExternalLink, Calendar, Settings as SettingsIcon,
  RefreshCw, Check, AlertTriangle,
} from "lucide-react";
import {
  useProperty, useCapabilitySample, useRefreshReviews,
} from "@/lib/queries";
import { Headline, formatDateAgo } from "@/components/VendorLens";
import { cn } from "@/lib/utils";


type Tab = "google" | "trustpilot" | "summary";


export default function PropertyReviews() {
  const { propertyId: rawId } = useParams<{ propertyId: string }>();
  const propertyId = Number(rawId);

  // ─── Hooks: ALL must be called unconditionally before any early
  // return. React tracks hooks by call-order; an early return that
  // skips a later hook on one render but not another → React error
  // #310 (the page goes blank). This bit me on F5 of /reviews when
  // property loaded async — first render hit isLoading→early-return
  // (3 hooks), second render data arrived→fell through (4 hooks),
  // hook-count mismatch crashed the tree. Keep new hooks ABOVE the
  // guard returns below.
  const property = useProperty(propertyId);
  const [tab, setTab] = useState<Tab>("summary");
  const refresh = useRefreshReviews(propertyId);

  if (property.isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!property.data) return <div className="text-sm text-red-600">Property not found.</div>;
  const p = property.data;

  const hasGbp = !!p.gbp_listing_name;
  const refreshResults = refresh.data?.results ?? [];
  const refreshOk = refresh.data ? refreshResults.filter(r => r.ok).length : 0;
  const refreshTotal = refresh.data ? refreshResults.length : 0;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              <h1 className="text-2xl font-semibold tracking-tight">Reviews</h1>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Review monitoring for <span className="font-mono">{p.domain}</span>{" "}
              across Google + Trustpilot. Data scraped via DataForSEO (the
              GBP API is quota-blocked, so DFS is the only path to review
              content).
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <button type="button" onClick={() => refresh.mutate({ force: true })}
              disabled={refresh.isPending}
              title={hasGbp
                ? "Force-refresh both Google + Trustpilot reviews (~30-90s)"
                : "Force-refresh Trustpilot reviews (~30-90s). Google needs GBP listing name."}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              <RefreshCw className={cn("h-3.5 w-3.5", refresh.isPending && "animate-spin")} />
              {refresh.isPending ? "Refreshing… (30-90s)" : "Refresh"}
            </button>
            {refresh.data && !refresh.isPending && (
              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-700 dark:text-emerald-400">
                <Check className="h-3 w-3" /> {refreshOk}/{refreshTotal} ok · {refresh.data.duration_s.toFixed(1)}s
              </span>
            )}
            {refresh.error && (
              <span className="inline-flex items-center gap-1 rounded-md bg-red-500/10 px-2 py-0.5 text-[11px] text-red-700 dark:text-red-400">
                <AlertTriangle className="h-3 w-3" /> {refresh.error.message}
              </span>
            )}
          </div>
        </div>
        {!hasGbp && (
          <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            <strong>Google reviews are off for this property.</strong>{" "}
            Set the <strong>GBP listing name</strong> on the{" "}
            <Link to={`/p/${propertyId}/settings`} className="inline-flex items-center gap-0.5 underline">
              <SettingsIcon className="h-3 w-3" /> Settings
            </Link>{" "}
            page (e.g. <span className="font-mono">nrg showroom Constanța</span>),
            then click Refresh. Trustpilot fires regardless.
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1 border-b border-border">
        <TabBtn active={tab === "summary"} onClick={() => setTab("summary")}>Summary</TabBtn>
        <TabBtn active={tab === "google"} onClick={() => setTab("google")}>Google reviews</TabBtn>
        <TabBtn active={tab === "trustpilot"} onClick={() => setTab("trustpilot")}>Trustpilot reviews</TabBtn>
      </div>

      {tab === "summary"    && <SummaryTab propertyId={propertyId} domain={p.domain} />}
      {tab === "google"     && <GoogleReviewsTab propertyId={propertyId} domain={p.domain} />}
      {tab === "trustpilot" && <TrustpilotReviewsTab propertyId={propertyId} domain={p.domain} />}
    </div>
  );
}


function TabBtn({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button type="button" onClick={onClick}
      className={cn(
        "border-b-2 px-3 py-2 text-sm font-medium transition-colors",
        active ? "border-primary text-foreground"
               : "border-transparent text-muted-foreground hover:text-foreground",
      )}>
      {children}
    </button>
  );
}


function SummaryTab({ propertyId, domain: _domain }: { propertyId: number; domain: string }) {
  const google = useCapabilitySample("dataforseo", "business_data_google_reviews_live", { propertyId });
  const tp = useCapabilitySample("dataforseo", "business_data_trustpilot_reviews_live", { propertyId });

  const g = (google.data?.response as Record<string, unknown> | undefined) ?? {};
  const t = (tp.data?.response as Record<string, unknown> | undefined) ?? {};
  const gItems = (g.items as unknown[] | undefined) ?? [];
  const tItems = (t.items as unknown[] | undefined) ?? [];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Headline label="Google reviews (with text)" value={gItems.length || undefined} sub={google.data?.fetched_at ? `fetched ${formatDateAgo(google.data.fetched_at)}` : undefined}/>
        <Headline label="Google rating"               value={g.rating_average as number | undefined} sub={g.rating_count != null ? `${g.rating_count} total ratings` : undefined}/>
        <Headline label="Trustpilot reviews"          value={tItems.length || undefined} sub={tp.data?.fetched_at ? `fetched ${formatDateAgo(tp.data.fetched_at)}` : undefined}/>
        <Headline label="Trustpilot rating"           value={t.rating_average as number | undefined} sub={t.rating_count != null ? `${t.rating_count} total ratings` : undefined}/>
      </div>

      {!google.data?.available && !tp.data?.available && (
        <Card>
          <div className="text-sm text-muted-foreground">
            <p className="font-medium">No review data yet.</p>
            <p className="mt-1">
              Go to the <strong>DataForSEO</strong> tab and click <strong>Refresh</strong>.
              Reviews use DFS's task-pattern endpoints which add ~30-90s each
              to the pull. Skip the bulk pull only if you don't need reviews.
            </p>
            <p className="mt-1 text-xs">
              Google reviews fire only when the property has a real
              display name (not just the domain). Trustpilot fires for
              every property using the bare domain as the profile slug.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}


function GoogleReviewsTab({ propertyId, domain: _domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("dataforseo", "business_data_google_reviews_live", { propertyId });
  const data = (sample.data?.response as Record<string, unknown> | undefined) ?? {};
  const items = (data.items as Array<Record<string, unknown>> | undefined) ?? [];

  return (
    <ReviewList
      title="Google reviews"
      ratingAverage={data.rating_average as number | undefined}
      ratingCount={data.rating_count as number | undefined}
      items={items}
      noDataLabel="No Google reviews data — pull from the DataForSEO tab. Only fires when display_name differs from the domain."
      sample={sample.data}
    />
  );
}


function TrustpilotReviewsTab({ propertyId, domain: _domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("dataforseo", "business_data_trustpilot_reviews_live", { propertyId });
  const data = (sample.data?.response as Record<string, unknown> | undefined) ?? {};
  const items = (data.items as Array<Record<string, unknown>> | undefined) ?? [];

  return (
    <ReviewList
      title="Trustpilot reviews"
      ratingAverage={data.rating_average as number | undefined}
      ratingCount={data.rating_count as number | undefined}
      items={items}
      noDataLabel="No Trustpilot reviews data — pull from the DataForSEO tab. The property may not have a Trustpilot profile."
      sample={sample.data}
    />
  );
}


function ReviewList({
  title, ratingAverage, ratingCount, items, noDataLabel, sample,
}: {
  title: string;
  ratingAverage: number | undefined;
  ratingCount: number | undefined;
  items: Array<Record<string, unknown>>;
  noDataLabel: string;
  sample: { fetched_at: string | null; available: boolean } | undefined;
}) {
  if (!sample?.available) {
    return (
      <Card>
        <div className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          {noDataLabel}
        </div>
      </Card>
    );
  }

  // Sentiment counts (stars-based)
  const ratingCounts = items.reduce((acc: Record<string, number>, r) => {
    const rt = (r.rating as Record<string, unknown> | undefined)?.value as number | undefined ?? r.rating as number | undefined;
    const k = rt ? `${Math.round(rt)}` : "?";
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold">{title}</h2>
              {ratingAverage != null && (
                <span className="inline-flex items-center gap-0.5 rounded bg-amber-500/10 px-1.5 py-0.5 text-xs text-amber-700 dark:text-amber-400">
                  <Star className="h-3 w-3 fill-current" />
                  {ratingAverage.toFixed(1)}
                  {ratingCount != null && (
                    <span className="text-[10px] text-muted-foreground"> · {ratingCount.toLocaleString()} ratings</span>
                  )}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Showing <strong>{items.length}</strong> review{items.length !== 1 ? "s" : ""} with text
              {ratingCount != null && ratingCount > items.length && (
                <> (of {ratingCount.toLocaleString()} total ratings — the other {(ratingCount - items.length).toLocaleString()} are star-only with no text, which Google doesn't expose)</>
              )}
              .
              {sample?.fetched_at && <> Fetched {formatDateAgo(sample.fetched_at)}.</>}
            </p>
          </div>
          {Object.keys(ratingCounts).length > 0 && (
            <div className="flex flex-col gap-0.5 text-[11px]">
              {[5, 4, 3, 2, 1].map((stars) => {
                const n = ratingCounts[String(stars)] ?? 0;
                const pct = items.length > 0 ? (n / items.length) * 100 : 0;
                return (
                  <div key={stars} className="flex items-center gap-1">
                    <span className="w-3 text-right text-muted-foreground">{stars}</span>
                    <Star className="h-2.5 w-2.5 text-amber-500" />
                    <div className="h-1.5 w-20 overflow-hidden rounded bg-muted">
                      <div className="h-full bg-amber-500/60" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-6 text-right text-muted-foreground">{n}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      <div className="space-y-2">
        {items.map((r, i) => <ReviewCard key={i} review={r} />)}
        {items.length === 0 && (
          <Card>
            <div className="text-sm text-muted-foreground text-center">No reviews returned.</div>
          </Card>
        )}
      </div>
    </div>
  );
}


function ReviewCard({ review: r }: { review: Record<string, unknown> }) {
  const rating = (r.rating as Record<string, unknown> | undefined)?.value as number | undefined
              ?? r.rating as number | undefined ?? null;
  const text = (r.review_text as string | undefined) ?? (r.text as string | undefined) ?? "";
  const author = ((r.profile as Record<string, unknown> | undefined)?.name as string | undefined)
              ?? (r.profile_name as string | undefined)
              ?? (r.username as string | undefined) ?? "Anonymous";
  const date = (r.timestamp as string | undefined) ?? (r.review_url_date as string | undefined) ?? "";
  const url = (r.review_url as string | undefined) ?? "";

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {rating != null && (
              <span className="inline-flex items-center gap-0.5 rounded bg-amber-500/10 px-1.5 py-0.5 text-[11px] text-amber-700 dark:text-amber-400">
                <Star className="h-2.5 w-2.5 fill-current" />
                {rating}
              </span>
            )}
            <span className="text-xs font-medium">{author}</span>
            {date && (
              <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground">
                <Calendar className="h-2.5 w-2.5" />
                {date.slice(0, 10)}
              </span>
            )}
          </div>
          {text && (
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{text}</p>
          )}
        </div>
        {url && (
          <a href={url} target="_blank" rel="noreferrer"
             className="shrink-0 text-muted-foreground hover:text-foreground" title="Open review">
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </Card>
  );
}
